import { Modal, ModalBody, ModalHeader, ProgressBar } from '@razorpay/blade/components';
import {
  WizardInfoProvider,
  useWizardInfo,
} from 'components/WizardViews/providers/WizardInfoProvider';
import { BonusRow } from 'components/Bonus/hooks/types';
import { bonusSource } from 'components/Bonus/types';
import { FormProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import React from 'react';
import BonusDetailsStep from './BonusDetailsStep';
import ClawbackRulesStep from './ClawbackRulesStep';
import { TOTAL_STEPS, bonusSteps, initialBonusFormData } from './constants';
import ReviewStep from './ReviewStep';
import { AllocateBonusModalProps, BonusFormData } from './types';
import { toPayrollMonth } from './utils';

const WizardProgress = () => {
  const { currentStep, totalSteps } = useWizardInfo();
  return (
    <ProgressBar
      accessibilityLabel={`Step ${currentStep + 1} of ${totalSteps}`}
      variant="meter"
      value={((currentStep + 1) / totalSteps) * 100}
    />
  );
};

const buildInitialFormData = (
  employeeName: string,
  peopleId: string,
  userOrgId: string,
  editBonus?: BonusRow | null,
): BonusFormData => {
  if (!editBonus) {
    return { ...initialBonusFormData, employeeName, peopleId, userOrgId };
  }
  const paymentStatus =
    editBonus.source === bonusSource.BONUS_SOURCE_OUTSIDE_XPAYROLL ? 'already_paid' : 'to_be_paid';
  const bonusAmount = editBonus.amount != null ? String(editBonus.amount) : '';
  const payoutMonth = toPayrollMonth(editBonus.payout_month);

  return {
    employeeName,
    peopleId,
    userOrgId,
    assignmentId: editBonus.assignment_id ?? '',
    bonusType: editBonus.bonus_component_id ?? '',
    bonusAmount,
    initialBonusAmount: bonusAmount,
    paymentStatus,
    initialPaymentStatus: paymentStatus,
    payoutMonth,
    initialPayoutMonth: payoutMonth,
    remark: editBonus.remarks ?? '',
    hasClawback: editBonus.clawback_applicable ? 'true' : 'false',
    clawbackMonths: editBonus.clawback_period ? String(editBonus.clawback_period) : '',
  };
};

export const AllocateBonusModal = ({
  isOpen,
  onClose,
  onAssignSuccess,
  employeeName,
  peopleId,
  userOrgId,
  editBonus,
}: AllocateBonusModalProps) => {
  const isEdit = Boolean(editBonus);
  const title = isEdit ? `Edit ${editBonus?.bonus_name ?? 'Bonus'}` : 'Allocate a Bonus';
  return (
    <Modal isOpen={isOpen} onDismiss={onClose} size="full" accessibilityLabel={title}>
      {isOpen && (
        <FormProvider
          initialState={buildInitialFormData(
            employeeName,
            peopleId ?? '',
            userOrgId ?? '',
            editBonus,
          )}>
          <WizardInfoProvider
            initialStep={0}
            totalSteps={TOTAL_STEPS}
            onWizardClose={onClose}
            onSuccess={onAssignSuccess}>
            <ModalHeader title={title} />
            <WizardProgress />
            <ModalBody height="100%" padding="spacing.0">
              <Wizard>
                <WizardStep step={bonusSteps.step1} component={BonusDetailsStep} />
                <WizardStep step={bonusSteps.step2} component={ClawbackRulesStep} />
                <WizardStep step={bonusSteps.step3} component={ReviewStep} />
              </Wizard>
            </ModalBody>
          </WizardInfoProvider>
        </FormProvider>
      )}
    </Modal>
  );
};

export default AllocateBonusModal;
